<script setup>
// Source → Selector Sankey. A 2-column flow (source ─► focus selector) whose link
// widths are event counts. Fed by the `source_log_types` array the event-search
// response already carries ([{ source, count, log_types }]); the parent
// rich view captures it from the Events.search(scope, 0, 0) call it already makes,
// so this widget adds no query. Source names arrive already collapsed to the group
// name for hide_sources groups (server-side), so nothing here can leak a hidden
// source's identity. Unlike the IP flow Sankey (ip↔port↔ip), there is no port/ip
// depth chain — this answers "which sources see this selector", not "how it talks".
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  // The event-search `source_log_types`: an array of
  // { source, count, log_types: [{ log_type, count }] }, one entry per source,
  // already count-desc. (Object is accepted only so the empty {} fallback some
  // callers pass doesn't trip prop validation; it's treated as "no data".)
  sourceCounts: {
    type: [Array, Object],
    default: () => [],
  },
  // Focus node label (the selector being viewed).
  selector: {
    type: String,
    required: true,
  },
  // The parent search is still in flight.
  loading: {
    type: Boolean,
    default: false,
  },
  // Cap on distinct source nodes; the long tail folds into an "Other" node.
  maxSources: {
    type: Number,
    default: 12,
  },
  height: {
    type: String,
    default: '360px',
  },
})

const emit = defineEmits(['source-click'])

const COLORS = {
  source: '#4A90D9',
  other: '#9CA3AF',
  focus: '#9B7EE8',
  focusGlow: 'rgba(115, 87, 210, 0.4)',
}

const FOCUS_ID = '__focus__'

function truncate(s, max) {
  const str = String(s ?? '')
  return str.length > max ? `${str.slice(0, max - 1)}…` : str
}

const chartContainer = ref(null)
let chartInstance = null
let resizeObserver = null

// Normalise the `source_log_types` array into [{ name, count }] — `count` is the
// per-source event total the backend already summed across log types. Blank/zero
// rows dropped, sorted by count desc.
const sources = computed(() => {
  const list = Array.isArray(props.sourceCounts) ? props.sourceCounts : []
  const rows = []
  for (const entry of list) {
    const name = String(entry?.source ?? '')
    const count = Number(entry?.count)
    if (name && Number.isFinite(count) && count > 0) {
      rows.push({ name, count })
    }
  }
  rows.sort((a, b) => b.count - a.count)
  return rows
})

// Build echarts nodes + links: top `maxSources` sources on the left, one focus
// node on the right, with the long tail collapsed into a single "Other" node.
const chartModel = computed(() => {
  const rows = sources.value
  if (!rows.length) return { nodes: [], links: [] }

  const head = rows.slice(0, props.maxSources)
  const tail = rows.slice(props.maxSources)

  const nodes = []
  const links = []

  head.forEach((s, i) => {
    const id = `src:${i}:${s.name}`
    nodes.push({
      id,
      label: s.name,
      count: s.count,
      isOther: false,
      itemStyle: { color: COLORS.source, borderColor: COLORS.source },
    })
    links.push({ source: id, target: FOCUS_ID, value: s.count })
  })

  if (tail.length) {
    const otherTotal = tail.reduce((sum, s) => sum + s.count, 0)
    if (otherTotal > 0) {
      const id = 'src:other'
      nodes.push({
        id,
        label: `Other (${tail.length} sources)`,
        count: otherTotal,
        isOther: true,
        itemStyle: { color: COLORS.other, borderColor: COLORS.other },
      })
      links.push({ source: id, target: FOCUS_ID, value: otherTotal })
    }
  }

  const focusTotal = head.reduce((s, r) => s + r.count, 0) +
    (tail.length ? tail.reduce((s, r) => s + r.count, 0) : 0)
  nodes.push({
    id: FOCUS_ID,
    label: props.selector,
    count: focusTotal,
    isFocus: true,
    itemStyle: {
      color: COLORS.focus,
      borderColor: COLORS.focus,
      shadowColor: COLORS.focusGlow,
      shadowBlur: 20,
    },
  })

  return { nodes, links }
})

const isEmpty = computed(() => chartModel.value.nodes.length === 0)

function updateChart() {
  if (!chartContainer.value) return
  if (!chartInstance) chartInstance = echarts.init(chartContainer.value)

  const { nodes, links } = chartModel.value
  if (!nodes.length || !links.length) {
    chartInstance.clear()
    return
  }

  const labelOf = {}
  const countOf = {}
  nodes.forEach((n) => {
    labelOf[n.id] = n.label
    countOf[n.id] = n.count
  })

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      backgroundColor: '#1E252F',
      borderColor: '#2a3441',
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: (params) => {
        if (params.dataType === 'node') {
          const label = labelOf[params.data.id] ?? params.data.id
          const count = countOf[params.data.id]
          const suffix = typeof count === 'number'
            ? `<br/>${count.toLocaleString()} events`
            : ''
          return `<strong>${label}</strong>${suffix}`
        }
        if (params.dataType === 'edge') {
          const src = labelOf[params.data.source] ?? params.data.source
          const tgt = labelOf[params.data.target] ?? params.data.target
          const value = typeof params.data.value === 'number'
            ? params.data.value.toLocaleString()
            : params.data.value
          return `${src} → ${tgt}<br/>Events: <strong>${value}</strong>`
        }
        return ''
      },
    },
    series: [{
      type: 'sankey',
      layout: 'none',
      // Small outer margins so the two columns pin to the card's left/right edges
      // and the link band fills the full width.
      left: 12,
      right: 12,
      top: 14,
      bottom: 14,
      emphasis: { focus: 'adjacency' },
      nodeAlign: 'justify',
      nodeGap: 10,
      nodeWidth: 16,
      draggable: false,
      data: nodes.map((n) => ({
        name: n.id,
        id: n.id,
        // Explicit depth guarantees exactly two columns (sources left, focus right)
        // regardless of the graph's inferred topology.
        depth: n.isFocus ? 1 : 0,
        itemStyle: n.itemStyle,
        draggable: false,
        label: {
          show: true,
          // Labels point INWARD (source names to the right of their node, the
          // focus name to the left of its node) so nothing is clipped at the card
          // edges — the earlier outward labels pushed the source column off-canvas.
          position: n.isFocus ? 'left' : 'right',
          distance: 6,
          color: n.isOther ? '#9CA3AF' : '#E5E7EB',
          fontSize: n.isFocus ? 14 : 11,
          fontWeight: n.isFocus ? 'bold' : 'normal',
          fontStyle: n.isOther ? 'italic' : 'normal',
          formatter: () => truncate(n.label, n.isFocus ? 28 : 20),
        },
      })),
      links: links.map((l) => ({ source: l.source, target: l.target, value: l.value })),
      lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.45 },
    }],
  }

  chartInstance.setOption(option, true)

  chartInstance.off('click')
  chartInstance.on('click', (params) => {
    // Every link runs source → focus, so a ribbon click resolves to the same
    // source node its left end sits on — clicking either is the same drilldown.
    const nodeId = params.dataType === 'node'
      ? params.data.id
      : params.dataType === 'edge'
        ? params.data.source
        : null
    if (!nodeId) return

    const node = chartModel.value.nodes.find((n) => n.id === nodeId)
    if (node && !node.isFocus && !node.isOther) {
      emit('source-click', { source: node.label, count: node.count })
    }
  })
}

watch(
  [() => props.sourceCounts, () => props.selector, () => props.loading],
  () => {
    if (chartContainer.value && !isEmpty.value) {
      setTimeout(() => updateChart(), 50)
    }
  },
  { deep: true },
)

function handleResize() {
  if (chartInstance) {
    try {
      chartInstance.resize()
    } catch (e) {
      console.error('Error resizing source sankey chart:', e)
    }
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  if (chartContainer.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => handleResize())
    resizeObserver.observe(chartContainer.value)
  }
  if (chartContainer.value && !isEmpty.value) {
    setTimeout(() => updateChart(), 100)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})

defineExpose({ resize: handleResize })
</script>

<template>
  <section class="source-sankey widget-section">
    <div class="section-header">
      <div class="section-heading">
        <h3 class="section-title">Sources</h3>
        <span class="section-subtitle">Event volume by source for this selector</span>
      </div>
    </div>
    <div class="sankey-fill" :style="{ minHeight: height }">
      <div v-if="loading && isEmpty" class="sankey-state loading">
        <font-awesome-icon icon="spinner" spin />
      </div>
      <div v-else-if="isEmpty" class="sankey-state">
        No sources observed for this selector
      </div>
      <!-- v-show (not v-if) keeps the ref alive across empty→data so the watcher
           can init/draw once counts arrive. -->
      <div
        v-show="!isEmpty"
        ref="chartContainer"
        class="sankey-chart"
        :style="{ height: height }"
      ></div>
    </div>
  </section>
</template>

<style scoped>
/* Grid placement (which columns/row) is owned by the host view — it knows where
   its Observations widget sits and pins this card beneath it. In non-grid hosts
   (e.g. IpView's stacked sections) the card simply fills its container. */
.source-sankey {
  min-width: 0;
}

.widget-section {
  background: var(--surface-card);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.section-heading {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0;
}

.section-subtitle {
  font-size: 0.8rem;
  color: var(--text-faint);
}

.sankey-fill {
  width: 100%;
}

.sankey-chart {
  width: 100%;
  cursor: pointer;
}

.sankey-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: inherit;
  height: 100%;
  color: var(--text-faint);
  font-size: 0.85rem;
}

.sankey-state.loading {
  color: var(--text-secondary);
  font-size: 1.5rem;
}
</style>
